// NIGHT-DIRECTSTAFF-2026-10-22 §6: what the installed app shows with no connection -- the last-loaded Today, Needs a human,
// Leads and Facilities lists (lib/offline.js snapshotOf: display rows only, no phone numbers), read-only, under a banner that
// says how old they are; and the "Sending when online" bar for anything written while offline.
import React, { useState } from 'react';
import { offlineLabel, isStale } from './lib/offline';

const TABS = [['today', 'Today'], ['needs', 'Needs a human'], ['leads', 'Leads'], ['facilities', 'Facilities']];
const when = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');
const li = { padding: '8px 12px', borderTop: '1px solid #f1f5f9', fontSize: 13.5, color: '#0f172a', overflowWrap: 'anywhere' };
const sub = { fontSize: 12.5, color: '#475569' };

export function OfflineBanner({ snap }) {
  return (
    <div data-testid="offline-banner" role="status" style={{ background: '#fef3c7', color: '#713f12', border: '1px solid #fcd34d', borderRadius: 8, padding: '8px 12px', fontSize: 13.5, fontWeight: 700, marginBottom: 10 }}>
      {offlineLabel(snap)}. These are the lists as they were then; nothing here can be changed until you're back online.
    </div>
  );
}

/**
 * queued: the outbox items; sending: true while they go out; online: the connection is back; whoOf(item) -> a name;
 * onCancel(id); onSendNow(id). Each item is listed "Waiting to send" with Cancel; one older than 30 minutes (back online)
 * asks "Send this now?" instead of going by itself.
 */
export function OutboxBar({ queued = [], sending = false, online = false, whoOf = () => '', onCancel = () => {}, onSendNow = () => {}, now = Date.now() }) {
  if (!queued.length) return null;
  const texts = queued.filter((q) => q.kind === 'text').length, emails = queued.length - texts;
  const what = [texts && `${texts} text${texts === 1 ? '' : 's'}`, emails && `${emails} email${emails === 1 ? '' : 's'}`].filter(Boolean).join(' and ');
  const b = (bg, fg) => ({ background: bg, color: fg, borderRadius: 8, padding: '0 12px', minHeight: 44, fontSize: 13, fontWeight: 700, border: 0, flexShrink: 0 });
  return (
    <div data-testid="outbox-bar" role="status" style={{ background: '#e0f2fe', color: '#075985', border: '1px solid #7dd3fc', borderRadius: 8, padding: '8px 12px', fontSize: 13.5, marginBottom: 10 }}>
      <div data-testid="outbox-summary" style={{ fontWeight: 700 }}>{sending ? `Sending ${what} now…` : `Sending when online: ${what}`}</div>
      <ul style={{ listStyle: 'none', margin: '4px 0 0', padding: 0 }}>
        {queued.map((q) => { const stale = online && isStale(q, now); const words = String((q.payload && (q.payload.subject || q.payload.body)) || '').slice(0, 80); return (
          <li key={q.id} data-testid={`outbox-item-${q.id}`} data-stale={stale ? 'true' : 'false'} style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', borderTop: '1px solid #bae6fd', padding: '4px 0' }}>
            <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{stale ? 'Send this now? ' : 'Waiting to send: '}{q.kind === 'text' ? 'text' : 'email'}{whoOf(q) ? ` to ${whoOf(q)}` : ''} · “{words}”</span>
            <span style={{ display: 'flex', gap: 6 }}>
              {stale && <button className="nb" data-testid={`outbox-send-${q.id}`} disabled={sending} onClick={() => onSendNow(q.id)} style={b('#0f766e', 'white')}>Send now</button>}
              <button className="nb" data-testid={`outbox-cancel-${q.id}`} disabled={sending} onClick={() => onCancel(q.id)} style={b('#f1f5f9', '#334155')}>Cancel</button>
            </span>
          </li>
        ); })}
      </ul>
    </div>
  );
}

export default function OfflineLists({ snap, initialTab = 'today' }) {
  const [tab, setTab] = useState(TABS.some(([k]) => k === initialTab) ? initialTab : 'today');
  if (!snap) {
    return (
      <div data-testid="offline-lists" className="card" style={{ padding: 20 }}>
        <p data-testid="offline-none" style={{ margin: 0, fontSize: 14, color: '#475569' }}>You're offline, and this device has no saved lists yet. They are saved the next time the CRM loads with a connection.</p>
      </div>
    );
  }
  const t = snap.today || { sections: [], total: 0 };
  return (
    <div data-testid="offline-lists">
      <OfflineBanner snap={snap} />
      <div role="group" aria-label="Saved lists" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        {TABS.map(([k, label]) => (
          <button key={k} className="nb" data-testid={`offline-tab-${k}`} aria-pressed={tab === k} onClick={() => setTab(k)}
            style={{ borderRadius: 20, padding: '0 16px', minHeight: 44, fontSize: 13, fontWeight: 700, background: tab === k ? '#0f2744' : '#f1f5f9', color: tab === k ? 'white' : '#334155' }}>{label}</button>
        ))}
      </div>
      {tab === 'today' && (
        <section data-testid="offline-today">
          <h2 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 8px', color: '#0f172a' }}>Today <span style={{ color: t.total ? '#b91c1c' : '#15803d' }}>{t.total}</span></h2>
          {t.sections.map((sec) => (
            <div key={sec.title} className="card" style={{ padding: 0, marginBottom: 10, overflow: 'hidden' }}>
              <h3 style={{ fontSize: 14, fontWeight: 800, margin: 0, padding: '8px 12px' }}>{sec.title} <span style={{ color: sec.items.length ? '#b91c1c' : '#475569' }}>{sec.items.length}</span></h3>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>{sec.items.map((x, i) => <li key={i} style={li}><strong>{x.name}</strong><div style={sub}>{x.line}</div></li>)}</ul>
            </div>
          ))}
        </section>
      )}
      {tab === 'needs' && (
        <ul data-testid="offline-needs" className="card" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {snap.needs.length === 0 && <li style={li}>Nobody was waiting.</li>}
          {snap.needs.map((r, i) => <li key={i} style={li}><strong>{r.name}</strong>{r.source && <span style={sub}> · {r.source}</span>}{r.asked && <div style={sub}>“{r.asked}”</div>}</li>)}
        </ul>
      )}
      {tab === 'leads' && (
        <ul data-testid="offline-leads" className="card" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {snap.leads.map((l, i) => <li key={i} style={li}><strong>{l.name}</strong><div style={sub}>{[l.discipline, l.state, l.source, when(l.at), l.handled ? 'handled' : ''].filter(Boolean).join(' · ')}</div></li>)}
        </ul>
      )}
      {tab === 'facilities' && (
        <ul data-testid="offline-facilities" className="card" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {snap.facilities.length === 0 && <li style={li}>No facility requests.</li>}
          {snap.facilities.map((f, i) => <li key={i} style={li}><strong>{f.facility}</strong>{f.status && <span style={sub}> · {f.status}</span>}
            <div style={sub}>{[f.contact, f.discipline, f.place, f.start && `start ${f.start}`, when(f.at)].filter(Boolean).join(' · ')}</div>{f.need && <div style={sub}>“{f.need}”</div>}</li>)}
        </ul>
      )}
    </div>
  );
}
